import React from 'react';

import styled from '@emotion/styled';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Group } from '@lightspeed/flame/Group';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

const LS_PAY_CONFIG_ROUTE = '/settings/lightspeed-payments/configuration?preauth=1';

const StyledModal = styled(Modal)`
  width: 540px;
  max-width: 95%;
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 4px;
  display: block;
  margin-bottom: 16px;
`;

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
`;

const CheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  line-height: 1.5;
`;

const GreenCheck: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    style={{ flexShrink: 0, marginTop: 2 }}
  >
    <path
      d="M2.5 7l3 3 5-5"
      stroke="#28a745"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface PreAuthPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PreAuthPromoModal: React.FC<PreAuthPromoModalProps> = ({ isOpen, onClose }) => {
  const isAdmin = Boolean(window.merchantos?.session?.hasRight('admin'));
  const shopName = window.merchantos?.session?.getShop()?.name || '';

  const handleGoToSettings = () => {
    onClose();
    window.PageRouter.goTo(LS_PAY_CONFIG_ROUTE);
  };

  return (
    <StyledModal isOpen={isOpen} shouldCloseOnOverlayClick onRequestClose={onClose}>
      <ModalHeader showCloseButton onCloseClicked={onClose}>
        <Text fontSize="14px">New: Preauthorization on Lightspeed Payments</Text>
      </ModalHeader>

      <ModalBody>
        <HeroImage
          src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/preauth-promo-hero.png"
          alt="Preauthorization feature illustration"
          data-test="preauth-promo-modal-hero"
        />

        <Text fontSize="18px" fontWeight="bold" mb={2} data-test="preauth-promo-modal-title">
          New: Preauthorization on Lightspeed Payments
        </Text>

        <Text size="small">
          Hold a deposit without charging your customer. Take the final payment later.
          Preauthorization is included with Lightspeed Payments at no additional cost.
        </Text>

        <CheckList>
          <CheckItem>
            <GreenCheck />
            <Text size="small">
              Secure a deposit before rental or service so you&apos;re covered if something goes
              wrong
            </Text>
          </CheckItem>
          <CheckItem>
            <GreenCheck />
            <Text size="small">
              Manage every hold, reminder and capture in one place within your POS
            </Text>
          </CheckItem>
          <CheckItem>
            <GreenCheck />
            <Text size="small">
              Save on processing fees—pay only when you capture, not when you hold
            </Text>
          </CheckItem>
        </CheckList>

        {!isAdmin && (
          <Alert type="info" noCloseBtn mt={3} data-test="preauth-promo-modal-admin-notice">
            An admin at <strong>{shopName}</strong> needs to enable preauthorization before you can
            use it.
          </Alert>
        )}
      </ModalBody>

      <ModalFooter>
        <Flex justifyContent="flex-end">
          {isAdmin ? (
            <Group>
              <Button onClick={onClose} data-test="preauth-promo-modal-close">
                Close
              </Button>
              <Button
                variant="secondary"
                fill
                onClick={handleGoToSettings}
                data-test="preauth-promo-modal-go-to-settings"
              >
                Go to Settings
              </Button>
            </Group>
          ) : (
            <Button onClick={onClose} data-test="preauth-promo-modal-close">
              Close
            </Button>
          )}
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

export default PreAuthPromoModal;
